import NotificationService from '@/services/NotificationService'

export default {
  data() {
    return {
      localNotificationState: {}
      // NO per-component flags - use global window flag only
    }
  },

  computed: {
    // Reactive access to notification state
    $notifications() {
      return NotificationService.getState().notifications
    },

    $unreadNotificationsCount() {
      return NotificationService.getState().unreadCount
    },

    $totalNotificationsCount() {
      return NotificationService.getState().totalCount
    },

    $notificationCategories() {
      return NotificationService.getState().categories
    },

    $notificationSummary() {
      return NotificationService.getState().summary
    },

    $notificationsLoading() {
      return NotificationService.getState().loading
    },

    // Helper computed properties
    $hasUnreadNotifications() {
      return this.$unreadNotificationsCount > 0
    },

    $formattedUnreadCount() {
      return NotificationService.getFormattedCount(this.$unreadNotificationsCount)
    },

    $unreadNotifications() {
      return this.$notifications.filter(n => !n.is_read)
    },

    $readNotifications() {
      return this.$notifications.filter(n => n.is_read)
    },

    $shouldSkipNotifications() {
      const publicRoutes = ['view-proposal', 'view-quotation', 'view-proforma-invoice', 'view-invoice']
      return publicRoutes.includes(this.$route?.name)
    },

    $isAuthenticated() {
      return !!localStorage.getItem('auth_token')
    },

    $shouldEnableNotifications() {
      return this.$isAuthenticated && !this.$shouldSkipNotifications()
    },

    $notificationsByCategory() {
      const grouped = {}
      this.$notifications.forEach(notification => {
        const category = notification.category || 'system'
        if (!grouped[category]) {
          grouped[category] = []
        }
        grouped[category].push(notification)
      })
      return grouped
    },

    $notificationsByPriority() {
      const grouped = {}
      this.$notifications.forEach(notification => {
        const priority = notification.priority || 'medium'
        if (!grouped[priority]) {
          grouped[priority] = []
        }
        grouped[priority].push(notification)
      })
      return grouped
    },

    $recentNotifications() {
      return this.$notifications.slice(0, 5)
    },

    $urgentNotifications() {
      return this.$notifications.filter(n => n.priority === 'urgent')
    },

    $isPollingActive() {
      return !!NotificationService.getState().polling
    }
  },

  created() {
    // Empty - no spam
  },

  mounted() {
    // 🌟 GLOBAL FLAG - ONLY ONE COMPONENT EVER RUNS THIS
    if (window._notificationMixinInitialized) return
    
    window._notificationMixinInitialized = true

    // ✅ DIRECT ROUTE CHECK - bulletproof
    const routeName = this.$route?.name
    const publicRoutes = ['view-proposal', 'view-quotation', 'view-proforma-invoice', 'view-invoice']
    const isPublic = publicRoutes.includes(routeName)
    const isAuth = !!localStorage.getItem('auth_token')
    const serviceState = NotificationService.getState()
    
    // console.log('🔔 Mixin MOUNTED (GLOBAL ONCE):', {
    //   routeName,
    //   isPublic,
    //   isAuth,
    //   serviceEnabled: serviceState.enabled,
    //   servicePolling: !!serviceState.polling,
    //   pathname: window.location.pathname
    // })
    
    // Enable ONLY if conditions met (router guard backup)
    if (isAuth && !isPublic) {
      //console.log('🧪 Mixin enabling notifications (GLOBAL ONCE)')
      NotificationService.enable()
    }
  },

  beforeUnmount() {
    if (this.$options.stopNotificationPollingOnDestroy) {
      this.$stopNotificationPolling()
    }
  },

  methods: {
    // ✅ DIRECT ROUTE CHECK - bulletproof, no computed dependency
    shouldSkipNotifications() {
      const publicRoutes = ['view-proposal', 'view-quotation', 'view-proforma-invoice', 'view-invoice']
      return publicRoutes.includes(this.$route?.name)
    },

    // Notification API methods
    async $fetchNotifications(options = {}) {
      if (this.shouldSkipNotifications()) return null
      
      try {
        return await NotificationService.fetchNotifications(options)
      } catch (error) {
        this.$handleNotificationError('Failed to fetch notifications', error)
        return null
      }
    },

    async $fetchNotificationSummary() {
      if (this.shouldSkipNotifications()) return null
      
      try {
        return await NotificationService.fetchSummary()
      } catch (error) {
        this.$handleNotificationError('Failed to fetch notification summary', error)
        return null
      }
    },

    async $fetchNotificationsByCategory(category, page = 1) {
      if (this.shouldSkipNotifications()) return null
      
      try {
        return await NotificationService.fetchByCategory(category, page)
      } catch (error) {
        this.$handleNotificationError('Failed to fetch category notifications', error)
        return null
      }
    },

    async $markNotificationAsRead(notificationId) {
      if (this.shouldSkipNotifications()) return false
      
      try {
        const result = await NotificationService.markAsRead(notificationId)
        return result
      } catch (error) {
        this.$handleNotificationError('Failed to mark notification as read', error)
        return false
      }
    },

    async $markAllNotificationsAsRead() {
      if (this.shouldSkipNotifications()) return false
      
      try {
        const result = await NotificationService.markAllAsRead()
        return result
      } catch (error) {
        this.$handleNotificationError('Failed to mark all notifications as read', error)
        return false
      }
    },

    async $deleteNotification(notificationId) {
      if (this.shouldSkipNotifications()) return false
      
      try {
        const result = await NotificationService.deleteNotification(notificationId)
        return result
      } catch (error) {
        this.$handleNotificationError('Failed to delete notification', error)
        return false
      }
    },

    async $clearReadNotifications() {
      if (this.shouldSkipNotifications()) return false
      
      try {
        const result = await NotificationService.clearReadNotifications()
        return result
      } catch (error) {
        this.$handleNotificationError('Failed to clear read notifications', error)
        return false
      }
    },

    // Utility methods
    $groupNotificationsByDate(notifications = null) {
      const notifs = notifications || this.$notifications
      return NotificationService.groupNotificationsByDate(notifs)
    },

    $filterNotifications(notifications = null, filters = {}) {
      const notifs = notifications || this.$notifications
      return NotificationService.filterNotifications(notifs, filters)
    },

    $getNotificationsByCategory(category) {
      return this.$notifications.filter(n => n.category === category)
    },

    $getNotificationsByPriority(priority) {
      return this.$notifications.filter(n => n.priority === priority)
    },

    $getNotificationsByStatus(isRead) {
      return this.$notifications.filter(n => n.is_read === isRead)
    },

    $getNotificationById(notificationId) {
      return this.$notifications.find(n => n.id === notificationId)
    },

    $getNotificationsFromToday() {
      const today = new Date().toDateString()
      return this.$notifications.filter(n => {
        const notificationDate = new Date(n.created_at).toDateString()
        return notificationDate === today
      })
    },

    $getNotificationsFromLastWeek() {
      const weekAgo = new Date()
      weekAgo.setDate(weekAgo.getDate() - 7)
      return this.$notifications.filter(n => {
        const notificationDate = new Date(n.created_at)
        return notificationDate >= weekAgo
      })
    },

    // Polling control methods
    $startNotificationPolling() {
      if (this.$shouldEnableNotifications()) {
        NotificationService.startPolling()
      }
    },

    $stopNotificationPolling() {
      NotificationService.stopPolling()
    },

    $updateNotificationPollingInterval(interval) {
      NotificationService.updatePollingInterval(interval)
    },

    $restartNotificationPolling() {
      this.$stopNotificationPolling()
      setTimeout(() => {
        this.$startNotificationPolling()
      }, 100)
    },

    // Error handling
    $handleNotificationError(message, error) {
      console.error(message, error)
      
      switch (error.type) {
        case 'validation':
          if (error.errors) {
            Object.keys(error.errors).forEach(field => {
              console.error(`${field}:`, error.errors[field])
            })
          }
          break
        case 'general':
          console.error('API Error:', error.message)
          break
        case 'network':
          console.warn('Network Error:', error.message)
          break
        case 'unauthenticated':
          this.$resetNotificationState()
          console.info('User unauthenticated, resetting notification state')
          break
        default:
          console.error('Unknown error:', error)
      }
    },

    $resetNotificationState() {
      NotificationService.resetState()
    },

    async $handleNotificationClick(notification) {
      try {
        if (!notification.is_read) {
          await this.$markNotificationAsRead(notification.id)
        }

        if (notification.action_url) {
          window.open(notification.action_url, '_blank')
        }

        return true
      } catch (error) {
        this.$handleNotificationError('Failed to handle notification click', error)
        return false
      }
    },

    // Batch operations
    async $batchMarkAsRead(notificationIds) {
      const results = []
      for (const id of notificationIds) {
        try {
          const result = await this.$markNotificationAsRead(id)
          results.push({ id, success: !!result })
        } catch (error) {
          results.push({ id, success: false, error })
        }
      }
      return results
    },

    async $batchDeleteNotifications(notificationIds) {
      const results = []
      for (const id of notificationIds) {
        try {
          const result = await this.$deleteNotification(id)
          results.push({ id, success: !!result })
        } catch (error) {
          results.push({ id, success: false, error })
        }
      }
      return results
    },

    async $batchMarkAsReadByCategory(category) {
      const categoryNotifications = this.$getNotificationsByCategory(category)
      const unreadIds = categoryNotifications
        .filter(n => !n.is_read)
        .map(n => n.id)
      
      if (unreadIds.length === 0) return []
      
      return await this.$batchMarkAsRead(unreadIds)
    },

    async $batchDeleteByCategory(category) {
      const categoryNotifications = this.$getNotificationsByCategory(category)
      const notificationIds = categoryNotifications.map(n => n.id)
      
      if (notificationIds.length === 0) return []
      
      return await this.$batchDeleteNotifications(notificationIds)
    },

    $searchNotifications(searchTerm, fields = ['title', 'message']) {
      if (!searchTerm) return this.$notifications
      
      const term = searchTerm.toLowerCase()
      return this.$notifications.filter(notification => {
        return fields.some(field => {
          const value = notification[field]
          return value && value.toLowerCase().includes(term)
        })
      })
    },

    $sortNotifications(notifications = null, sortBy = 'created_at', sortOrder = 'desc') {
      const notifs = [...(notifications || this.$notifications)]
      
      return notifs.sort((a, b) => {
        let aValue = a[sortBy]
        let bValue = b[sortBy]
        
        if (sortBy === 'created_at' || sortBy === 'read_at') {
          aValue = new Date(aValue || 0)
          bValue = new Date(bValue || 0)
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase()
          bValue = bValue.toLowerCase()
        }
        
        let result = 0
        if (aValue < bValue) result = -1
        if (aValue > bValue) result = 1
        
        return sortOrder === 'desc' ? -result : result
      })
    },

    $getNotificationStats() {
      const total = this.$totalNotificationsCount
      const unread = this.$unreadNotificationsCount
      const read = total - unread
      
      return {
        total,
        unread,
        read,
        readPercentage: total > 0 ? Math.round((read / total) * 100) : 0,
        unreadPercentage: total > 0 ? Math.round((unread / total) * 100) : 0,
        categoryCounts: { ...this.$notificationCategories },
        priorityCounts: this.$getPriorityCounts(),
        todayCount: this.$getNotificationsFromToday().length,
        weekCount: this.$getNotificationsFromLastWeek().length
      }
    },

    $getPriorityCounts() {
      const counts = { urgent: 0, high: 0, medium: 0, low: 0 }
      this.$notifications.forEach(notification => {
        const priority = notification.priority || 'medium'
        if (counts.hasOwnProperty(priority)) {
          counts[priority]++
        }
      })
      return counts
    },

    async $refreshNotifications(showLoader = true) {
      if (this.shouldSkipNotifications()) return false
      
      if (showLoader) {
        // Show loading indicator
      }
      
      try {
        await this.$fetchNotificationSummary()
        await this.$fetchNotifications()
        return true
      } catch (error) {
        this.$handleNotificationError('Failed to refresh notifications', error)
        return false
      }
    },

    async $forceRefreshNotifications() {
      this.$stopNotificationPolling()
      await this.$refreshNotifications()
      this.$startNotificationPolling()
    }
  },

  watch: {
    $isAuthenticated: {
      immediate: true,
      handler(isAuthenticated) {
        // Router guard handles enable/disable now
      }
    }
  }
}
